import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { QuestionnaireService } from '../../services/questionnaireService';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useLanguage } from '../../context/LanguageContext';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import { JourneyApiService } from '../../services/journeyApiService';
import { supabase } from '../../services/supabaseClient';
import { JourneyBackButton } from '../../components/journey/JourneyBackButton';
import { ContentLanguageMetadataService } from '../../services/contentLanguageMetadataService';
import '../../styles/pages/CreateQuestionnairePage.css';
import '../../styles/layout/wide-screen-layouts.css';

interface QuestionOption {
  id: string;
  text: string;
}

interface QuestionDraft {
  id: string;
  text: string;
  options: QuestionOption[];
  type: 'single' | 'multiple';
}

interface QuestionPreviewCardProps {
  question: QuestionDraft;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const QuestionPreviewCard: React.FC<QuestionPreviewCardProps> = ({
  question,
  index,
  isEditing,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
  canMoveUp,
  canMoveDown
}) => {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasMoreThanTwoOptions = question.options.length > 2;
  const visibleOptions = isExpanded ? question.options : question.options.slice(0, 2);
  
  return (
    <div 
      className={`question-preview-card ${isEditing ? 'editing-active' : ''}`}
      onClick={onEdit}
      title={t('createQuestionnaire.clickToEdit')}
    >
      <div className="question-preview-header">
        <div className="question-preview-title">
          <h6 title={`${index + 1}. ${question.text}`}>
            {index + 1}. {question.text}
          </h6>
          <div>
            <span className={`badge bg-${question.type === 'multiple' ? 'success' : 'info'}`}>
              {t(`createQuestionnaire.${question.type === 'multiple' ? 'multipleChoice' : 'singleChoice'}`)}
            </span>
            {isEditing && (
              <span className="badge bg-warning ms-2">
                <i className="bi bi-pencil-square me-1"></i>{t('createQuestionnaire.editing')}
              </span>
            )}
          </div>
        </div>
        
        <div className="question-preview-controls">
          <div className="btn-group" role="group" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={onMoveUp} 
              className="btn btn-sm btn-outline-secondary" 
              disabled={!canMoveUp} 
              title={t('createQuestionnaire.moveUp')}
              type="button"
            >
              <i className="bi bi-arrow-up-short"></i>
            </button>
            <button 
              onClick={onMoveDown} 
              className="btn btn-sm btn-outline-secondary" 
              disabled={!canMoveDown} 
              title={t('createQuestionnaire.moveDown')}
              type="button"
            >
              <i className="bi bi-arrow-down-short"></i>
            </button>
            <button 
              onClick={onEdit} 
              className={`btn btn-sm ${isEditing ? 'btn-warning' : 'btn-outline-primary'}`}
              title={t('createQuestionnaire.editQuestion')}
              type="button"
            >
              <i className="bi bi-pencil-square"></i>
            </button>
            <button 
              onClick={onDelete} 
              className="btn btn-sm btn-danger" 
              title={t('createQuestionnaire.deleteQuestion')}
              type="button"
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
      
      <ul className={`list-unstyled ${isExpanded ? 'question-options-expanded' : 'question-options-collapsed'}`}>
        {visibleOptions.map((option, optIdx) => (
          <li key={optIdx}>{option.text}</li>
        ))}
      </ul>
      
      {hasMoreThanTwoOptions && (
        <button 
          className="show-more-options"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          type="button"
        >
          {isExpanded 
            ? t('createQuestionnaire.showLess', { count: question.options.length - 2 })
            : t('createQuestionnaire.showMore', { count: question.options.length - 2 })
          }
        </button>
      )}
    </div>
  );
};

interface QuestionEditModalProps {
  question: QuestionDraft | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (question: QuestionDraft) => void;
}

const QuestionEditModal: React.FC<QuestionEditModalProps> = ({
  question,
  isOpen,
  onClose,
  onSave
}) => {
  const { t } = useLanguage();
  const [editText, setEditText] = useState('');
  const [editType, setEditType] = useState<'single' | 'multiple'>('single');
  const [editOptions, setEditOptions] = useState<QuestionOption[]>([]);
  const [newOptionText, setNewOptionText] = useState('');

  useEffect(() => {
    if (question) {
      setEditText(question.text);
      setEditType(question.type);
      setEditOptions([...question.options]);
    }
  }, [question]);

  const handleAddOption = () => {
    if (newOptionText.trim()) {
      const newOption: QuestionOption = {
        id: Date.now().toString(),
        text: newOptionText.trim()
      };
      setEditOptions([...editOptions, newOption]);
      setNewOptionText('');
    }
  };

  const handleUpdateOption = (optionId: string, newText: string) => {
    setEditOptions(editOptions.map(opt => 
      opt.id === optionId ? { ...opt, text: newText } : opt
    ));
  };

  const handleRemoveOption = (optionId: string) => {
    setEditOptions(editOptions.filter(opt => opt.id !== optionId));
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...editOptions];
    const item = newOptions[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (swapIndex < 0 || swapIndex >= newOptions.length) return;

    newOptions[index] = newOptions[swapIndex];
    newOptions[swapIndex] = item;
    setEditOptions(newOptions);
  };

  const handleSave = () => {
    const minOptions = editType === 'single' ? 2 : 1;
    if (!editText.trim() || editOptions.length < minOptions) {
      return;
    }

    if (question) {
      onSave({
        ...question,
        text: editText,
        type: editType,
        options: editOptions
      });
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !question) return null;

  return (
    <div className="question-edit-modal" onClick={handleBackdropClick}>
      <div className="question-edit-modal-content">
        <div className="question-edit-modal-header">
          <h3 className="question-edit-modal-title">{t('createQuestionnaire.editQuestionTitle')}</h3>
          <button 
            className="question-edit-modal-close"
            onClick={onClose}
            type="button"
            aria-label={t('createQuestionnaire.closeModal')}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
        
        <div className="question-edit-modal-body">
          <div className="modal-form-group">
            <label className="modal-form-label" htmlFor="modal-question-text">
              {t('createQuestionnaire.questionTextRequired')}
            </label>
            <textarea
              id="modal-question-text"
              className="modal-form-control textarea"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder={t('createQuestionnaire.enterQuestionHere')}
              rows={3}
            />
          </div>

          <div className="modal-form-group">
            <label className="modal-form-label" htmlFor="modal-question-type">
              {t('createQuestionnaire.questionTypeRequired')}
            </label>
            <select
              id="modal-question-type"
              className="modal-form-control"
              value={editType}
              onChange={(e) => setEditType(e.target.value as 'single' | 'multiple')}
            >
              <option value="single">{t('createQuestionnaire.singleChoice')}</option>
              <option value="multiple">{t('createQuestionnaire.multipleChoice')}</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label className="modal-form-label">
              {t('createQuestionnaire.minimumOptions', { min: editType === 'single' ? 2 : 1 })}
            </label>
            <div className="modal-options-builder">
              <div className="modal-options-list">
                {editOptions.map((option, index) => (
                  <div key={option.id} className="modal-option-item">
                    <div className="modal-option-number">
                      {index + 1}
                    </div>
                    <textarea
                      className="modal-option-input"
                      value={option.text}
                      onChange={(e) => handleUpdateOption(option.id, e.target.value)}
                      placeholder={t('createQuestionnaire.enterOptionText')}
                      rows={1}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = 'auto';
                        target.style.height = Math.max(48, target.scrollHeight) + 'px';
                      }}
                    />
                    <div className="modal-option-controls">
                      <button
                        onClick={() => handleMoveOption(index, 'up')}
                        className="btn btn-sm btn-outline-secondary"
                        disabled={index === 0}
                        title={t('createQuestionnaire.moveUp')}
                        type="button"
                      >
                        <i className="bi bi-arrow-up-short"></i>
                      </button>
                      <button
                        onClick={() => handleMoveOption(index, 'down')}
                        className="btn btn-sm btn-outline-secondary"
                        disabled={index === editOptions.length - 1}
                        title={t('createQuestionnaire.moveDown')}
                        type="button"
                      >
                        <i className="bi bi-arrow-down-short"></i>
                      </button>
                      <button
                        onClick={() => handleRemoveOption(option.id)}
                        className="btn btn-sm btn-outline-danger"
                        title={t('createQuestionnaire.removeOptionTitle')}
                        type="button"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="modal-add-option-form">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={newOptionText}
                    onChange={(e) => setNewOptionText(e.target.value)}
                    placeholder={t('createQuestionnaire.addNewOption')}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                  />
                  <button 
                    className="btn btn-outline-primary"
                    onClick={handleAddOption}
                    disabled={!newOptionText.trim()}
                    type="button"
                  >
                    <i className="bi bi-plus-lg me-1"></i>{t('createQuestionnaire.addOption')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="question-edit-modal-footer">
          <button 
            className="btn btn-secondary"
            onClick={onClose}
            type="button"
          >
            {t('createQuestionnaire.cancel')}
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!editText.trim() || editOptions.length < (editType === 'single' ? 2 : 1)}
            type="button"
          >
            <i className="bi bi-check-lg me-1"></i>{t('createQuestionnaire.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateQuestionnairePage: React.FC = () => {
  const { user } = useAuth();
  const { selectedCountry } = useCountry();
  const { contentLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  
  // Determine if we're in edit mode
  const isEditMode = Boolean(questionnaireId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rewardPoints, setRewardPoints] = useState(50);
  const [maxParticipants, setMaxParticipants] = useState<number | undefined>();
  const [skillRequired, setSkillRequired] = useState<string>('');

  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [currentQText, setCurrentQText] = useState('');
  const [currentOptions, setCurrentOptions] = useState<QuestionOption[]>([]);
  const [currentQType, setCurrentQType] = useState<'single' | 'multiple'>('single');
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [newOptionText, setNewOptionText] = useState('');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionDraft | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set initial loading state based on what needs to be loaded
  useEffect(() => {
    const needsTemplateLoading = searchParams.has('stepId') && searchParams.has('journeyId');
    const needsEditLoading = isEditMode && questionnaireId;
    if (needsTemplateLoading || needsEditLoading) {
      setLoading(true);
    }
  }, [isEditMode, questionnaireId, searchParams]);

  // Load template data if coming from journey workflow
  useEffect(() => {
    const loadTemplate = async () => {
      const templateId = searchParams.get('template');
      const stepId = searchParams.get('stepId');
      const journeyId = searchParams.get('journeyId');
      const source = searchParams.get('source');
      
      console.log('🎯 CreateQuestionnairePage: Loading template data with params:', {
        templateId,
        stepId,
        journeyId,
        source,
        allParams: Object.fromEntries(searchParams.entries())
      });
      
      if (stepId && journeyId) {
        try {
          console.log('📞 Calling prepareIntegrationData...');
          const integrationData = await JourneyPlatformIntegrationService.prepareIntegrationData(
            stepId,
            'survey',
            templateId || undefined, // Allow undefined for contextual matching
            journeyId
          );
          
          console.log('📦 Integration data received:', {
            templateId: integrationData.templateId,
            hasTemplate: !!integrationData.template,
            templateTitle: integrationData.template?.title
          });
          
          if (integrationData.template) {
            const templateData = JourneyPlatformIntegrationService.extractTemplateData(integrationData.template, contentLanguage);
            
            console.log('📋 Extracted template data:', templateData);
            console.log('🌐 Using language:', contentLanguage);
            
            // Pre-fill form with template data
            if (templateData.title) {
              console.log('✏️ Setting title:', templateData.title);
              setTitle(templateData.title);
            }
            if (templateData.description) {
              console.log('✏️ Setting description:', templateData.description);
              setDescription(templateData.description);
            }
            if (templateData.rewardPoints) {
              console.log('✏️ Setting rewardPoints:', templateData.rewardPoints);
              setRewardPoints(templateData.rewardPoints);
            }
            if (templateData.maxParticipants) {
              console.log('✏️ Setting maxParticipants:', templateData.maxParticipants);
              setMaxParticipants(templateData.maxParticipants);
            }
            if (templateData.skillRequired) {
              console.log('✏️ Setting skillRequired:', templateData.skillRequired);
              setSkillRequired(templateData.skillRequired);
            }
            
            // Pre-fill questions if available
            if (templateData.questions && Array.isArray(templateData.questions)) {
              console.log('✏️ Setting questions:', templateData.questions.length, 'questions');
              const questionDrafts: QuestionDraft[] = templateData.questions.map((q: any, index: number) => {
                // Handle both old format (string) and new format (object)
                if (typeof q === 'string') {
                  return {
                    id: `template-${index}`,
                    text: q,
                    options: [],
                    type: 'single' as const
                  };
                } else {
                  // New object format with structured questions
                  // Map question type: 'multiple-choice' or 'text' to 'single' or 'multiple'
                  let questionType: 'single' | 'multiple' = 'single';
                  if (q.type === 'text' || !q.options || q.options.length === 0) {
                    // Text questions have no options, treat as single (will be converted to text input)
                    questionType = 'single';
                  } else if (q.type === 'multiple-choice' || q.type === 'single') {
                    questionType = 'single';
                  } else if (q.type === 'multiple') {
                    questionType = 'multiple';
                  }
                  
                  return {
                    id: `template-${index}`,
                    text: q.question || q.text || '',
                    options: Array.isArray(q.options) ? q.options.map((opt: any, optIndex: number) => ({
                      id: `opt-${index}-${optIndex}`,
                      text: typeof opt === 'string' ? opt : (opt.text || opt)
                    })) : [],
                    type: questionType
                  };
                }
              });
              setQuestions(questionDrafts);
            }
          } else {
            console.log('❌ No template found in integration data');
          }
        } catch (err) {
          console.error('❌ Failed to load template:', err);
          setError('Failed to load template data. You can still create your questionnaire manually.');
        } finally {
          // Only set loading to false if we're not in edit mode (edit mode has its own loading)
          if (!isEditMode) {
            setLoading(false);
          }
        }
      } else {
        console.log('ℹ️ Not loading template - missing stepId or journeyId');
        // Only set loading to false if we're not in edit mode (edit mode has its own loading)
        if (!isEditMode) {
          setLoading(false);
        }
      }
    };

    loadTemplate();
  }, [searchParams]);

  // Load existing questionnaire data if in edit mode
  useEffect(() => {
    const loadExistingQuestionnaire = async () => {
      if (!isEditMode || !questionnaireId || !user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('📝 Loading questionnaire for editing:', questionnaireId);
        const { data: questionnaireData, error: questionnaireError } = await QuestionnaireService.getQuestionnaireDetails(questionnaireId);
        
        if (questionnaireError) {
          const errorMessage = (questionnaireError as any)?.message || String(questionnaireError);
          throw new Error(`Failed to load questionnaire: ${errorMessage}`);
        }
        
        if (!questionnaireData) {
          throw new Error('Questionnaire not found');
        }
        
        // Check if user owns this questionnaire
        if (questionnaireData.publisher_id !== user.id) {
          throw new Error('You do not have permission to edit this questionnaire');
        }
        
        // Populate form with existing data
        setTitle(questionnaireData.title || '');
        setDescription(questionnaireData.description || '');
        setRewardPoints(questionnaireData.reward_points || 50);
        setMaxParticipants(questionnaireData.max_participants || undefined);
        setSkillRequired(questionnaireData.skill_required || '');
        
        // Load questions using the dedicated editing method
        console.log('Loading questions for editing questionnaire:', questionnaireId);
        const questionsResult = await QuestionnaireService.getQuestionnaireForEditing(questionnaireId, user.id);
        
        if (questionsResult.error) {
          console.error('Failed to load questions for editing:', questionsResult.error);
          throw new Error(`Failed to load questions: ${(questionsResult.error as any)?.message || String(questionsResult.error)}`);
        }
        
        if (questionsResult.questions && questionsResult.questions.length > 0) {
          // Convert questions to draft format
          const questionDrafts: QuestionDraft[] = questionsResult.questions.map((q: any) => ({
            id: q.id,
            text: q.question_text,
            type: q.question_type === 'multiple_choice' ? 'multiple' : 'single',
            options: (q.options || []).map((opt: any) => ({
              id: opt.id,
              text: opt.option_text
            }))
          }));
          setQuestions(questionDrafts);
          console.log('✅ Loaded', questionDrafts.length, 'questions for editing');
        } else {
          console.log('⚠️ No questions found for questionnaire:', questionnaireId);
        }
        
        console.log('✅ Questionnaire loaded for editing');
        
      } catch (err) {
        console.error('❌ Failed to load questionnaire for editing:', err);
        setError(err instanceof Error ? err.message : 'Failed to load questionnaire for editing');
      } finally {
        setLoading(false);
      }
    };

    loadExistingQuestionnaire();
  }, [isEditMode, questionnaireId, user]);

  // Option management functions
  const addOption = () => {
    if (!newOptionText.trim()) return;
    const newOption: QuestionOption = {
      id: Date.now().toString(),
      text: newOptionText.trim()
    };
    setCurrentOptions([...currentOptions, newOption]);
    setNewOptionText('');
  };

  const updateOption = (optionId: string, newText: string) => {
    setCurrentOptions(currentOptions.map(opt => 
      opt.id === optionId ? { ...opt, text: newText } : opt
    ));
  };

  const removeOption = (optionId: string) => {
    setCurrentOptions(currentOptions.filter(opt => opt.id !== optionId));
  };

  const moveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...currentOptions];
    const item = newOptions[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (swapIndex < 0 || swapIndex >= newOptions.length) return;

    newOptions[index] = newOptions[swapIndex];
    newOptions[swapIndex] = item;
    setCurrentOptions(newOptions);
  };

  const handleSaveQuestionDraft = () => {
    const minOptions = currentQType === 'single' ? 2 : 1;
    if (!currentQText.trim() || currentOptions.length < minOptions) {
      const questionType = t(`createQuestionnaire.${currentQType === 'single' ? 'singleChoice' : 'multipleChoice'}`);
      setError(t('createQuestionnaire.errorMinOptions', { 
        type: questionType, 
        min: minOptions, 
        plural: minOptions > 1 ? 's' : '' 
      }));
      return;
    }
    setError(null);
    
    if (editingQuestionId) {
      setQuestions(questions.map(q => 
        q.id === editingQuestionId 
          ? { ...q, text: currentQText, options: currentOptions, type: currentQType } 
          : q
      ));
      setEditingQuestionId(null);
    } else {
      setQuestions([...questions, { 
        id: Date.now().toString(), 
        text: currentQText, 
        options: currentOptions, 
        type: currentQType 
      }]);
    }
    
    // Reset form
    setCurrentQText('');
    setCurrentOptions([]);
    setCurrentQType('single');
  };

  const startEditQuestion = (id: string) => {
    const questionToEdit = questions.find(q => q.id === id);
    if (questionToEdit) {
      setEditingQuestion(questionToEdit);
      setIsModalOpen(true);
    }
  };

  const handleModalSave = (updatedQuestion: QuestionDraft) => {
    setQuestions(questions.map(q => 
      q.id === updatedQuestion.id ? updatedQuestion : q
    ));
    setIsModalOpen(false);
    setEditingQuestion(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingQuestion(null);
  };

  const cancelEdit = () => {
    setEditingQuestionId(null);
    setCurrentQText('');
    setCurrentOptions([]);
    setCurrentQType('single');
    setNewOptionText('');
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const newQuestions = [...questions];
    const item = newQuestions[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (swapIndex < 0 || swapIndex >= newQuestions.length) return;

    newQuestions[index] = newQuestions[swapIndex];
    newQuestions[swapIndex] = item;
    setQuestions(newQuestions);
  };

  const deleteQuestionDraft = (id: string) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const formattedQuestions = questions.map((q, index) => {
        const question_type: "single_choice" | "multiple_choice" = q.type === 'single' ? 'single_choice' : 'multiple_choice';
        return {
          question_text: q.text,
          question_type,
          position: index + 1,
          options: q.options.map((opt, optIndex) => ({
            option_text: opt.text,
            position: optIndex + 1,
          })),
        };
      });

      const { data: questionnaire, error } = isEditMode && questionnaireId
        ? await QuestionnaireService.updateQuestionnaire(questionnaireId, {
            title,
            description,
            rewardPoints,
            maxParticipants,
            skillRequired,
            questions: formattedQuestions,
            publisherId: user.id,
            countryCode: selectedCountry,
          })
        : await QuestionnaireService.createQuestionnaire({
            title,
            description,
            rewardPoints,
            maxParticipants,
            skillRequired,
            questions: formattedQuestions,
            publisherId: user.id,
            countryCode: selectedCountry,
          });

      if (error) throw error;
      if (!questionnaire) throw new Error(isEditMode ? 'Failed to update questionnaire' : 'Failed to create questionnaire');

      // Store language metadata in localStorage
      ContentLanguageMetadataService.setContentLanguage(
        'survey',
        questionnaire.id,
        contentLanguage as 'en' | 'zh'
      );

      // Track completion if this came from a journey workflow
      const stepId = searchParams.get('stepId');
      if (stepId && questionnaire.id) {
        try {
          await JourneyPlatformIntegrationService.trackActionCompletion(
            stepId,
            'survey',
            questionnaire.id
          );
        } catch (trackingError) {
          console.error('Failed to track action completion:', trackingError);
          // Don't fail the whole operation if tracking fails
        }
      }

      // Redirect to the questionnaire page
      navigate('/questionnaires/publisher');
    } catch (err) {
      console.error(isEditMode ? 'Error updating questionnaire:' : 'Error creating questionnaire:', err);
      setError(err instanceof Error ? err.message : (isEditMode ? 'Failed to update questionnaire' : 'Failed to create questionnaire'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="create-questionnaire-page">
        {/* Journey Back Button */}
        <JourneyBackButton variant="floating" />
        
        {loading && (
          <div className="loading-container">
            <div className="loading-ring"></div>
            <p>{t('createQuestionnaire.loadingData')}</p>
          </div>
        )}
        <div className="form-container">
          {/* Main Form Section */}
          <div className="form-main">
            <form onSubmit={handleSubmit} className="create-questionnaire-form">
              {error && <div className="alert alert-danger">{error}</div>}

              {/* Basic Information Section */}
              <div className="form-section">
                <h3>{t('createQuestionnaire.basicInformation')}</h3>
                <div className="create-task-form-group">
                  <label htmlFor="title" className="create-task-form-label">{t('createQuestionnaire.titleRequired')}</label>
                  <input 
                    id="title"
                    className="form-control" 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('createQuestionnaire.titlePlaceholder')}
                    required
                  />
                </div>
                
                <div className="create-task-form-group">
                  <label htmlFor="description" className="create-task-form-label">{t('createQuestionnaire.description')}</label>
                  <textarea 
                    id="description"
                    className="form-control" 
                    rows={3}
                    value={description} 
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('createQuestionnaire.descriptionPlaceholder')}
                  />
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="create-task-form-group">
                      <label htmlFor="rewardPoints" className="create-task-form-label">{t('createQuestionnaire.rewardPointsRequired')}</label>
                      <input
                        id="rewardPoints"
                        type="number"
                        className="form-control"
                        value={rewardPoints}
                        onChange={(e) => setRewardPoints(parseInt(e.target.value, 10))}
                        min="1"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="create-task-form-group">
                      <label htmlFor="maxParticipants" className="create-task-form-label">{t('createQuestionnaire.maxParticipants')}</label>
                      <input
                        id="maxParticipants"
                        type="number"
                        className="form-control"
                        value={maxParticipants ?? ''}
                        onChange={(e) => setMaxParticipants(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                        placeholder={t('createQuestionnaire.maxParticipantsPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="create-task-form-group">
                  <label htmlFor="skillRequired" className="create-task-form-label">{t('createQuestionnaire.skillRequired')}</label>
                  <input 
                    id="skillRequired"
                    className="form-control" 
                    value={skillRequired} 
                    onChange={(e) => setSkillRequired(e.target.value)}
                    placeholder={t('createQuestionnaire.skillRequiredPlaceholder')}
                  />
                </div>
              </div>

              <div className="layout-section">
                <h3>
                  {t('createQuestionnaire.addQuestion')}
                  {editingQuestionId && (
                    <span className="text-warning ms-2">
                      <i className="bi bi-pencil-square me-1"></i>
                      <small>{t('createQuestionnaire.editingQuestion')}</small>
                    </span>
                  )}
                </h3>
                <div className="create-task-form-group">
                  <label htmlFor="questionText" className="create-task-form-label">{t('createQuestionnaire.questionTextRequired')}</label>
                  <input 
                    id="questionText"
                    className="form-control" 
                    value={currentQText} 
                    onChange={(e) => setCurrentQText(e.target.value)}
                    placeholder={t('createQuestionnaire.questionTextPlaceholder')}
                  />
                </div>
                
                <div className="create-task-form-group">
                  <label htmlFor="questionType" className="create-task-form-label">{t('createQuestionnaire.questionTypeRequired')}</label>
                  <select 
                    id="questionType"
                    className="form-control" 
                    value={currentQType} 
                    onChange={(e) => setCurrentQType(e.target.value as 'single' | 'multiple')}
                  >
                    <option value="single">{t('createQuestionnaire.singleChoice')}</option>
                    <option value="multiple">{t('createQuestionnaire.multipleChoice')}</option>
                  </select>
                </div>

                {/* Options Builder */}
                <div className="create-task-form-group">
                  <label className="create-task-form-label">{t('createQuestionnaire.answerOptionsRequired')}</label>
                  <div className="options-builder">
                    {/* Current Options List */}
                    {currentOptions.length > 0 && (
                      <div className="options-list mb-3">
                        {currentOptions.map((option, index) => (
                          <div key={option.id} className="option-item">
                            <div className="option-number">{index + 1}</div>
                            <input
                              type="text"
                              className="form-control"
                              value={option.text}
                              onChange={(e) => updateOption(option.id, e.target.value)}
                              placeholder={`Option ${index + 1}`}
                            />
                            <div className="option-controls">
                              <button
                                type="button"
                                onClick={() => moveOption(index, 'up')}
                                disabled={index === 0}
                                className="btn btn-sm btn-outline-secondary"
                                title={t('createQuestionnaire.moveUpTitle')}
                              >
                                <i className="bi bi-arrow-up"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => moveOption(index, 'down')}
                                disabled={index === currentOptions.length - 1}
                                className="btn btn-sm btn-outline-secondary"
                                title={t('createQuestionnaire.moveDownTitle')}
                              >
                                <i className="bi bi-arrow-down"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => removeOption(option.id)}
                                className="btn btn-sm btn-outline-danger"
                                title={t('createQuestionnaire.removeOption')}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Option */}
                    <div className="add-option-form">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={newOptionText}
                          onChange={(e) => setNewOptionText(e.target.value)}
                          placeholder={t('createQuestionnaire.addNewOption')}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addOption();
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={addOption}
                          className="btn btn-outline-primary"
                          disabled={!newOptionText.trim()}
                        >
                          <i className="bi bi-plus-lg"></i> {t('createQuestionnaire.addOption')}
                        </button>
                      </div>
                      <small className="text-muted">
                        {t('createQuestionnaire.minimumOptions', { min: currentQType === 'single' ? 2 : 1 })}
                        {currentOptions.length > 0 && ` (${currentOptions.length} ${t('common.selected').toLowerCase()})`}
                      </small>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex gap-2 mb-3">
                  <button type="button" onClick={handleSaveQuestionDraft} className={`btn ${editingQuestionId ? 'btn-success' : 'btn-info'}`}>
                    <i className={`bi ${editingQuestionId ? 'bi-check-circle' : 'bi-plus-circle-dotted'} me-2`}></i>
                    {editingQuestionId ? t('createQuestionnaire.updateQuestion') : t('createQuestionnaire.saveQuestion')}
                  </button>
                  {editingQuestionId && (
                    <button type="button" onClick={cancelEdit} className="btn btn-outline-secondary">
                      <i className="bi bi-x-circle me-2"></i>{t('createQuestionnaire.cancelEdit')}
                    </button>
                  )}
                </div>
              </div>

              <button className="btn btn-primary" type="submit" disabled={loading || questions.length === 0}>
                {loading 
                  ? (isEditMode ? t('createQuestionnaire.updating') : t('createQuestionnaire.creating')) 
                  : (isEditMode ? t('createQuestionnaire.updateButton') : t('createQuestionnaire.createButton'))
                }
              </button>
            </form>
          </div>

          {/* Questions Preview Section */}
          <div className="preview-container">
            <h3 className="preview-title">{t('createQuestionnaire.questionsCount', { count: questions.length })}</h3>
            {questions.length === 0 && (
              <div className="text-muted text-center py-4">
                <i className="bi bi-patch-question display-4 d-block mb-2"></i>
                <p>{t('createQuestionnaire.noQuestionsYet')}<br/>{t('createQuestionnaire.noQuestionsHelp')}</p>
              </div>
            )}
            
            {questions.length > 0 && (
              <>
                <p className="text-muted small mb-3">
                  <i className="bi bi-info-circle me-1"></i>
                  {t('createQuestionnaire.clickToEditHelp')}
                </p>
                <div className="questions-list">
                  {questions.map((q, idx) => (
                    <QuestionPreviewCard 
                      key={q.id}
                      question={q}
                      index={idx}
                      isEditing={editingQuestion?.id === q.id}
                      onEdit={() => startEditQuestion(q.id)}
                      onMoveUp={() => moveQuestion(idx, 'up')}
                      onMoveDown={() => moveQuestion(idx, 'down')}
                      onDelete={() => deleteQuestionDraft(q.id)}
                      canMoveUp={idx > 0}
                      canMoveDown={idx < questions.length - 1}
                    />
                  ))}
                </div>
                
                <div className="preview-tips mt-4">
                  <h6>{t('createQuestionnaire.tipsTitle')}</h6>
                  <ul className="text-sm">
                    <li>{t('createQuestionnaire.tip1')}</li>
                    <li>{t('createQuestionnaire.tip2')}</li>
                    <li>{t('createQuestionnaire.tip3')}</li>
                    <li>{t('createQuestionnaire.tip4')}</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Question Edit Modal */}
        <QuestionEditModal
          question={editingQuestion}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      </div>
    </BasePageLayout>
  );
};

export default CreateQuestionnairePage;
